/**
 * V103-3-6 / V103-6-3 — client for the GovRAMP overview, tier change, program
 * events (GovRAMP grants / lapse notices, the shared Ready booking, ConMon
 * items done, Class B bookings), ConMon submit and the FedRAMP Class A items.
 *
 * Every call is workspace-scoped (`/api/w/:slug/*`): the server resolves the
 * workspace and the caller from the session. Reads: any member. Writes: owner /
 * admin or the program lead (a Ready booking also the FedRAMP lead; Class A
 * materials owner / admin or the FedRAMP lead) — server-enforced.
 */
import type {
  AlternativeFrameworkMaterial, ClassAClock, ClassAMaterialsItem, FrameworkReadiness, GovrampOverview, GovrampTierChange, GovrampTierDelta, ProgramEvent,
} from '@aigovops/core';
import { ApiError, req } from './api';

export type { GovrampOverview, GovrampTierDelta, ProgramEvent, ClassAMaterialsItem, ClassAClock, AlternativeFrameworkMaterial, FrameworkReadiness };

const w = (slug: string) => `/api/w/${encodeURIComponent(slug)}`;
const prog = (slug: string, program: string) => `${w(slug)}/programs/${encodeURIComponent(program)}`;
const post = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });

export const getGovrampOverview = (slug: string, program = 'govramp') =>
  req<{ overview: GovrampOverview }>(`${prog(slug, program)}/govramp`).then((r) => r.overview);

export const getGovrampTierDelta = (slug: string, program: string, to: string) =>
  req<{ delta: GovrampTierDelta }>(`${prog(slug, program)}/govramp/tier-delta?to=${encodeURIComponent(to)}`).then((r) => r.delta);

export const changeGovrampTier = (slug: string, program: string, body: { to_tier: string; effective_from?: string }) =>
  req<{ change: GovrampTierChange }>(`${prog(slug, program)}/govramp/tier-change`, post(body));

export type ProgramEventInput =
  | { kind: 'govramp_status_granted'; data: { tier: string; granted_on: string } }
  | { kind: 'govramp_lapse_notice'; data: { notice_date: string; note?: string } }
  | { kind: 'ready_booking'; data: { firm: string; assessment_date: string } }
  | { kind: 'conmon_item_done'; data: { cycle_id: string; item: string } }
  | { kind: 'higher_class_booking'; data: { firm: string; assessment_date: string } };

export const recordProgramEvent = (slug: string, program: string, body: ProgramEventInput) =>
  req<{ event: ProgramEvent }>(`${prog(slug, program)}/events`, post(body)).then((r) => r.event);

export const endProgramEvent = (slug: string, program: string, eventId: string, reason?: string) =>
  req<{ event: ProgramEvent }>(`${prog(slug, program)}/events/${encodeURIComponent(eventId)}/end`, post(reason ? { reason } : {})).then((r) => r.event);

export const listProgramEvents = (slug: string, program: string, kinds: readonly string[] = []) =>
  req<{ events: ProgramEvent[] }>(`${prog(slug, program)}/events${kinds.length ? `?kind=${encodeURIComponent(kinds.join(','))}` : ''}`).then((r) => r.events);

export const generateConmonCycles = (slug: string, program: string, periodId: string) =>
  req<{ created: number }>(`${prog(slug, program)}/periods/${encodeURIComponent(periodId)}/conmon-cycles/generate`, post({}));

export const submitConmonCycle = (slug: string, program: string, periodId: string, cycleId: string, body: { snapshot_score_percent?: number; notes?: string } = {}) =>
  req<{ cycle: unknown }>(`${prog(slug, program)}/periods/${encodeURIComponent(periodId)}/conmon-cycles/${encodeURIComponent(cycleId)}/submit`, post(body));

// ---- FedRAMP Class A (V103-6-4 / 6-8 routes) ----------------------------------------------------

export interface ClassAView { profile_id: string; certification_class: string | null; items: [ClassAMaterialsItem, ...ClassAClock[]] }

export const getClassA = (slug: string, profileId: string) =>
  req<ClassAView>(`${w(slug)}/certification-profiles/${encodeURIComponent(profileId)}/class-a`);

export const setClassAListing = (slug: string, profileId: string, body: { class_a_listing_date?: string | null; higher_class_assessment_scheduled_on?: string | null; class_a_target_listing_date?: string | null }) =>
  req<{ clocks: ClassAClock[] }>(`${w(slug)}/certification-profiles/${encodeURIComponent(profileId)}/class-a-listing`, { method: 'PUT', body: JSON.stringify(body) });

export const listAlternativeMaterials = (slug: string, profileId: string) =>
  req<{ materials: AlternativeFrameworkMaterial[] }>(`${w(slug)}/certification-profiles/${encodeURIComponent(profileId)}/alternative-materials`).then((r) => r.materials);

/** Upload a file and record it as a Class A material (or replace `supersedes`). Multipart. */
export async function uploadAlternativeMaterial(slug: string, profileId: string, input: {
  file: File; framework: string; kind: string; assessedAt: string; title?: string; supersedes?: string;
}): Promise<AlternativeFrameworkMaterial> {
  const form = new FormData();
  form.set('file', input.file);
  form.set('framework', input.framework);
  form.set('kind', input.kind);
  form.set('assessed_at', input.assessedAt);
  if (input.title) form.set('title', input.title);
  if (input.supersedes) form.set('supersedes', input.supersedes);
  const res = await fetch(`${w(slug)}/certification-profiles/${encodeURIComponent(profileId)}/alternative-materials/upload`, { method: 'POST', body: form }); // raw-fetch-ok: FormData multipart upload
  if (!res.ok) {
    let message = res.statusText;
    let parsed: unknown;
    try { parsed = await res.json(); const b = parsed as { error?: string; detail?: string }; if (b?.error) message = b.detail ? `${b.error} — ${b.detail}` : b.error; } catch { /* not JSON */ }
    throw new ApiError(res.status, message, parsed);
  }
  return ((await res.json()) as { material: AlternativeFrameworkMaterial }).material;
}

/** FedRAMP requirements in scope for a class (required; `optional` adds the optional ones). */
export const getClassReadiness = (slug: string, cls: string, optional = false) =>
  req<FrameworkReadiness>(`${w(slug)}/frameworks/fedramp-cr26/readiness?class=${encodeURIComponent(cls)}${optional ? '&optional=1' : ''}`);
